import { formatDateInputValue, formatDateTimeInputValue } from '../../shared/utils/localization';
import type { AvailabilitySlot } from './types/Calendar';

export const AVAILABILITY_LIST_PAGE_SIZE = 50;

export type AvailabilityDayGroup = {
  dayKey: string;
  dayLabel: string;
  slots: AvailabilitySlot[];
};

const toDayKey = (value: string) => formatDateInputValue(value);

export const groupAvailabilitySlotsByDay = (
  slots: AvailabilitySlot[],
  formatDayLabel: (startAtUtc: string) => string,
): AvailabilityDayGroup[] => {
  const groups = new Map<string, AvailabilityDayGroup>();

  for (const slot of slots) {
    const dayKey = toDayKey(slot.startAtUtc);
    if (!dayKey) {
      continue;
    }

    let group = groups.get(dayKey);
    if (!group) {
      group = {
        dayKey,
        dayLabel: formatDayLabel(slot.startAtUtc),
        slots: [],
      };
      groups.set(dayKey, group);
    }

    group.slots.push(slot);
  }

  return Array.from(groups.values());
};

export const mergeAvailabilitySlots = (
  current: AvailabilitySlot[],
  incoming: AvailabilitySlot[],
): AvailabilitySlot[] => {
  if (incoming.length === 0) {
    return current;
  }

  const seen = new Set(current.map((slot) => `${slot.startAtUtc}|${slot.endAtUtc}|${slot.roomId || ''}|${slot.staffId || ''}`));
  const merged = [...current];

  for (const slot of incoming) {
    const key = `${slot.startAtUtc}|${slot.endAtUtc}|${slot.roomId || ''}|${slot.staffId || ''}`;
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    merged.push(slot);
  }

  return merged;
};

export const clampEventDurationMinutes = (value: number): number => (
  Math.max(5, Math.min(1440, Number.isFinite(value) ? Math.round(value) : 20))
);

export const getDurationMinutesBetweenDates = (startAt: Date, endAt: Date): number | null => {
  if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime()) || endAt <= startAt) {
    return null;
  }

  return Math.round((endAt.getTime() - startAt.getTime()) / 60000);
};

export const addDurationMinutesToDate = (startAt: Date, durationMinutes: number): Date => (
  new Date(startAt.getTime() + clampEventDurationMinutes(durationMinutes) * 60000)
);

export const buildAvailabilityFromLocal = (
  anchorDate: Date,
  openingStartMinute: number | null,
): string => {
  const dateOnly = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate());
  const startMinutes = openingStartMinute ?? (9 * 60);
  dateOnly.setHours(Math.floor(startMinutes / 60), startMinutes % 60, 0, 0);
  return formatDateTimeInputValue(dateOnly);
};

export const resolveAvailabilityQueryMinutes = (options: {
  slotMinutes: number;
  durationMinutes: number;
}): { slotMinutes: number; durationMinutes: number } => {
  const baseSlotMinutes = Math.max(5, Math.min(240, Number.isFinite(options.slotMinutes) ? options.slotMinutes : 20));
  const durationMinutes = clampEventDurationMinutes(options.durationMinutes);

  return {
    slotMinutes: Math.min(baseSlotMinutes, durationMinutes),
    durationMinutes,
  };
};

export const getAvailabilitySlotDurationMinutes = (slot: AvailabilitySlot): number | null => {
  const startAt = new Date(slot.startAtUtc);
  const endAt = new Date(slot.endAtUtc);
  if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime()) || endAt <= startAt) {
    return null;
  }

  return Math.round((endAt.getTime() - startAt.getTime()) / 60000);
};
